import { Node, mergeAttributes } from "@tiptap/core";

const MIN_WIDTH = 120;

/**
 * Video — a block node holding an uploaded video URL, rendered as an inline <video controls>
 * player. Carries an optional persisted pixel `width` (with a corner drag handle when editable),
 * mirroring ResizableImage. Round-trips through the editor's JSON serialization, so no extra
 * backend handling is needed beyond hosting the file.
 */
const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      title: { default: null },
      width: {
        default: null,
        parseHTML: (element) => {
          const attr = element.getAttribute("width");
          if (attr) return parseInt(attr, 10) || null;
          const styleWidth = element.style?.width;
          if (styleWidth && styleWidth.endsWith("px"))
            return parseInt(styleWidth, 10) || null;
          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return {
            width: attributes.width,
            style: `width: ${attributes.width}px; max-width: 100%; height: auto;`,
          };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "video[src]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "video",
      mergeAttributes(HTMLAttributes, {
        controls: "true",
        preload: "metadata",
      }),
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: options }),
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      let currentNode = node;

      const container = document.createElement("div");
      container.className = "resizable-video";

      const video = document.createElement("video");
      video.controls = true;
      video.preload = "metadata";

      // Same reason as ResizableImage's applyAttrs: a reused node view gets a different node
      // through `update`, so `src` has to be re-read there and not only on create.
      const applyAttrs = () => {
        const { src, width } = currentNode.attrs;
        if (video.getAttribute("src") !== src) video.setAttribute("src", src ?? "");
        video.style.width = width ? `${width}px` : "";
      };

      applyAttrs();
      container.appendChild(video);

      const handle = document.createElement("span");
      handle.className = "resize-handle";
      container.appendChild(handle);

      let startX = 0;
      let startWidth = 0;

      const onPointerMove = (e) => {
        const delta = e.clientX - startX;
        const maxWidth = container.parentElement?.clientWidth ?? Infinity;
        let next = Math.round(startWidth + delta);
        next = Math.max(MIN_WIDTH, Math.min(next, maxWidth));
        video.style.width = `${next}px`;
      };

      const onPointerUp = () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        const finalWidth = parseInt(video.style.width, 10);
        if (typeof getPos === "function" && !Number.isNaN(finalWidth)) {
          editor
            .chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, {
                ...currentNode.attrs,
                width: finalWidth,
              });
              return true;
            })
            .run();
        }
      };

      handle.addEventListener("pointerdown", (e) => {
        if (!editor.isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        startX = e.clientX;
        startWidth = video.offsetWidth;
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
      });

      return {
        dom: container,
        update: (updatedNode) => {
          if (updatedNode.type.name !== currentNode.type.name) return false;
          currentNode = updatedNode;
          applyAttrs();
          return true;
        },
      };
    };
  },
});

export default Video;
